import React, { useEffect, useState } from 'react';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios, { type AxiosResponse } from 'axios';

import { NavLinkButton, Panel, Table } from '@shared/components/bootstrap';
import { InputFormControl } from '@shared/components/fields/input_form_control';
import { useDebounce } from '@shared/hooks';

import { Permission } from '@admin/components/helpers/permission';
import { Titleize } from '@admin/components/helpers/titleize';
import { Spinner } from '@admin/components/spinner';
import { useAxiosRequest } from '@admin/hooks';
import { useNavigate } from '@admin/hooks/use_navigate';
import { useTitle } from '@admin/hooks/use_title';
import { MARKETING } from '@admin/libraries/access_control_catalog.generated';

import { AffiliateDeleteButton } from './delete_button';

type Affiliate = {
  id: number;
  kind: string;
  name: string;
  email: string;
  phone: string;
  payout: number;
  discount: number;
};

const useAffiliatesRequest = (query?: string) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Affiliate[]>> | undefined>(undefined);
  useEffect(() => {
    setRequest(() => axios.get('/marketing/affiliates.json', { params: { query: query } }));
  }, [query]);
  return useAxiosRequest<Affiliate[]>(request);
};

export const AffiliatesList = () => {
  useTitle('Affiliates');
  const navigate = useNavigate();
  const [query, setQuery] = useState<string | undefined>();
  const { response } = useAffiliatesRequest(useDebounce(query));
  const affiliates = response?.data;

  return (
    <Panel>
      <Panel.Body>
        <InputFormControl
          className="mb-4"
          type="text"
          value={query ?? ''}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by name, email, or phone"
        />
        <Table striped>
          <thead>
            <tr>
              <th className="col-sm-1">ID</th>
              <th className="col-sm-1">Type</th>
              <th className="col-sm-2">Name</th>
              <th className="col-sm-2">Email</th>
              <th className="col-sm-2">Phone</th>
              <th className="col-sm-2">Payout #</th>
              <th className="col-sm-2">Discount</th>
              <Permission resource={MARKETING.resource} action={MARKETING.actions.update} denied={null}>
                <th />
                <th />
              </Permission>
            </tr>
          </thead>
          <tbody>
            {affiliates?.map((affiliate) => (
              <tr key={affiliate.id}>
                <td className="col-sm-1">{affiliate.id}</td>
                <td className="col-sm-1">{affiliate.kind && <Titleize text={affiliate.kind} />}</td>
                <td className="col-sm-2">
                  <a href={`/marketing/affiliates/${affiliate.id}`}>{affiliate.name}</a>
                </td>
                <td className="col-sm-2">{affiliate.email}</td>
                <td className="col-sm-2">{affiliate.phone}</td>
                <td className="col-sm-2">${affiliate.payout}</td>
                <td className="col-sm-2">${affiliate.discount}</td>

                <Permission resource={MARKETING.resource} action={MARKETING.actions.update} denied={null}>
                  <td className="col-zero">
                    <AffiliateDeleteButton affiliate={affiliate} onDelete={() => navigate('/marketing/affiliates')} />
                  </td>
                  <td className="col-zero">
                    <NavLinkButton to={`/marketing/affiliates/${affiliate.id}/edit`} kind="primary">
                      Edit
                    </NavLinkButton>
                  </td>
                </Permission>
              </tr>
            ))}
          </tbody>
        </Table>
        {!affiliates && <Spinner />}
      </Panel.Body>

      <Permission resource={MARKETING.resource} action={MARKETING.actions.update} denied={null}>
        <Panel.Footer align="right">
          <NavLinkButton to="/marketing/affiliates/new" kind="mint">
            <FontAwesomeIcon icon={faPlus} /> Add
          </NavLinkButton>
        </Panel.Footer>
      </Permission>
    </Panel>
  );
};
